import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const AVATAR = "/geo-avatar.png";

function genSession() {
  return "sess-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// Minimal **bold** renderer that preserves line breaks
function renderText(text) {
  return text.split("\n").map((line, li) => (
    <span key={li} className="block">
      {line.split(/(\*\*[^*]+\*\*)/g).map((part, pi) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={pi} className="font-semibold text-white">
            {part.slice(2, -2)}
          </strong>
        ) : (
          <span key={pi}>{part}</span>
        )
      )}
    </span>
  ));
}

const SUGGESTIONS = [
  "How much can I earn?",
  "Where do I get the dashcam?",
  "How do I withdraw?",
];

export const AssistantWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(genSession);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi! I'm Geo, your GeoNetix assistant. Ask me anything about earning in INR, the dashcam, or how it all works.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      if (!res.ok) throw new Error("Request failed");
      const data = await res.json();
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Sorry, I couldn't reach the assistant right now. Please try again, or use our Contact page.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <motion.button
        data-testid="assistant-launcher"
        onClick={() => setOpen((v) => !v)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, type: "spring", stiffness: 260, damping: 20 }}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-5 right-5 z-[60] flex h-16 w-16 items-center justify-center rounded-full overflow-hidden border border-white/15 bg-[#0A192F] shadow-[0_10px_30px_-6px_rgba(0,180,216,0.6)]"
        aria-label="Open GeoNetix assistant"
      >
        {!open && (
          <span className="absolute inset-0 rounded-full bg-gradient-to-br from-[#00B4D8]/25 to-[#4CAF50]/25" />
        )}
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span
              key="x"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              className="text-[#00B4D8]"
            >
              <X size={26} />
            </motion.span>
          ) : (
            <motion.img
              key="avatar"
              src={AVATAR}
              alt="Geo assistant"
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.6, opacity: 0 }}
              className="relative h-full w-full object-contain object-bottom drop-shadow"
            />
          )}
        </AnimatePresence>
        {!open && (
          <span className="absolute bottom-1 right-1 h-3 w-3 rounded-full bg-[#4CAF50] border-2 border-[#0A192F]" />
        )}
      </motion.button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="assistant-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-5 z-[60] w-[calc(100vw-2.5rem)] max-w-[380px] h-[540px] max-h-[75vh] flex flex-col overflow-hidden rounded-3xl border border-white/15 bg-[#0A192F] shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center gap-3 border-b border-white/10 bg-black px-5 py-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full overflow-hidden bg-gradient-to-br from-[#00B4D8]/25 to-[#4CAF50]/25 border border-[#00B4D8]/40">
                <img src={AVATAR} alt="Geo" className="h-full w-full object-contain object-bottom" />
              </div>
              <div>
                <div className="font-semibold text-sm text-white">Geo Assistant</div>
                <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#4CAF50]" /> Online · GeoNetix AI
                </div>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`assistant-msg-${m.role}`}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#4CAF50] text-[#04140a] rounded-br-sm"
                        : "bg-white/5 border border-white/10 text-slate-200 rounded-bl-sm"
                    }`}
                  >
                    {m.role === "assistant" ? renderText(m.content) : m.content}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="rounded-2xl rounded-bl-sm bg-white/5 border border-white/10 px-4 py-3">
                    <div className="flex gap-1.5">
                      {[0, 1, 2].map((d) => (
                        <motion.span
                          key={d}
                          className="h-2 w-2 rounded-full bg-[#00B4D8]"
                          animate={{ opacity: [0.3, 1, 0.3] }}
                          transition={{ duration: 1, repeat: Infinity, delay: d * 0.2 }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {messages.length <= 1 && !loading && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid="assistant-suggestion"
                      onClick={() => send(s)}
                      className="rounded-full border border-[#00B4D8]/40 bg-[#00B4D8]/5 px-3 py-1.5 text-xs text-[#00B4D8] hover:bg-[#00B4D8]/15 transition-colors duration-200"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="border-t border-white/10 bg-black p-3 flex items-center gap-2"
            >
              <input
                data-testid="assistant-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about earnings, dashcam..."
                className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00B4D8]/60"
              />
              <button
                data-testid="assistant-send"
                type="submit"
                disabled={loading || !input.trim()}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#00B4D8] text-[#04140a] disabled:opacity-40 transition-opacity"
                aria-label="Send message"
              >
                <Send size={17} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
